import React, { useState, useEffect, useRef } from "react";
import {
  FaUserCircle,
  // FaSearch,
  FaShoppingBag,
  FaThLarge,
  FaStore,
  FaBars,
  FaTimes,
  FaSearch,
} from "react-icons/fa";
import { Link, useNavigate, useLocation } from "react-router-dom";
import AnimatedLogo from "./AnimatedLogo";

export default function Header() {
  const [showAuth, setShowAuth] = useState(false);
  const [isLogin, setIsLogin] = useState(true);
  const [user, setUser] = useState(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [authSubmitting, setAuthSubmitting] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const menuRef = useRef();
  const authRef = useRef();
  const dropdownRef = useRef();
  const ADMIN_ROLES = [2, 3]; // 2 - Менеджер, 3 - Администратор
  const [search, setSearch] = useState("");
  const PRIVACY_LINK = "./Soglashenie"; // ссылка на документ политики

  useEffect(() => {
    const token = localStorage.getItem("access_token");
    const login = localStorage.getItem("login");
    const role = localStorage.getItem("role");
    const role_id = localStorage.getItem("role_id");
    if (token && login) setUser({ login, role, role_id: Number(role_id) });
  }, []);

  useEffect(() => setMenuOpen(false), [location]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
        setDropdownOpen(false);
      }
      if (authRef.current && !authRef.current.contains(e.target)) {
        setShowAuth(false);
      }
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLogout = () => {
    localStorage.clear();
    setUser(null);
    setDropdownOpen(false);
    setMenuOpen(false);
    navigate("/");
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (search.trim().length > 0) {
      navigate(`/price?q=${encodeURIComponent(search.trim())}`);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    const login = e.target.login.value;
    const password = e.target.password.value;
    const password2 = e.target.password2?.value;
    const agree = e.target.agree?.checked;

    // Валидация только для РЕГИСТРАЦИИ: латиница/цифры/ASCII, без кириллицы
    if (!isLogin) {
      const RE_ASCII_ANY = /^[\x20-\x7E]+$/; // пробел..~ (любой печатный ASCII)
      const RE_ASCII_NO_SPACE = /^[!-~]+$/; // !..~ (ASCII без пробела)

      if (!RE_ASCII_NO_SPACE.test(login)) {
        setError(
          "Логин: только латинские буквы, цифры и спецсимволы (без пробелов и кириллицы)"
        );
        return;
      }
      if (!RE_ASCII_ANY.test(password) || !RE_ASCII_ANY.test(password2)) {
        setError(
          "Пароль: только латинские буквы, цифры и спецсимволы (без кириллицы)"
        );
        return;
      }
      if (password !== password2) {
        setError("Пароли не совпадают");
        return;
      }
      if (!agree) {
        setError(
          "Для регистрации необходимо согласиться с обработкой персональных данных"
        );
        return;
      }
    }

    if (!isLogin && password !== password2) {
      setError("Пароли не совпадают");
      return;
    }

    const url = isLogin
      ? "/api/accounts/token/"
      : "/api/accounts/registration/";
    const payload = isLogin
      ? { login, password }
      : { login, password1: password, password2 };
    setAuthSubmitting(true);
    try {
      const res = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json", // <- просим JSON
        },
        body: JSON.stringify(payload),
      });

      // аккуратно читаем ответ
      const ct = res.headers.get("content-type") || "";
      let body;
      if (ct.includes("application/json")) {
        body = await res.json();
      } else {
        const text = await res.text();
        body = {
          detail:
            text && res.status < 500
              ? text
              : "Ошибка сервера. Повторите позже.",
        };
      }

      if (res.ok) {
        if (isLogin) {
          localStorage.setItem("access_token", body.access);
          localStorage.setItem("refresh_token", body.refresh);
          localStorage.setItem("login", login);
          localStorage.setItem("role", body.role || "");
          localStorage.setItem("role_id", body.role_id ?? "");
          setUser({ login, role: body.role, role_id: body.role_id });
          setShowAuth(false);
        } else {
          setSuccess("Регистрация прошла успешно");
          setIsLogin(true);
        }
      } else {
        let msg = body?.detail;
        // detail может быть строкой / массивом / словарём
        if (Array.isArray(msg)) {
          msg = msg.join(" ");
        } else if (!msg && body && typeof body === "object") {
          try {
            msg = Object.values(body).flat().join(" ");
          } catch (_) {
            msg = JSON.stringify(body);
          }
        }
        if (msg === "No active account found with the given credentials") {
          msg = "Неверный логин или пароль";
        }
        setError(msg || `Ошибка ${res.status}`);
      }
    } catch (err) {
      console.error("Ошибка при авторизации:", err);
      setError("Ошибка подключения к серверу");
    } finally {
      setAuthSubmitting(false);
    }
  };

  return (
    <header className="bg-white shadow border-b sticky top-0 z-50">
      <div className="max-w-7xl mx-auto flex justify-between items-center h-16 px-4">
        <div className="flex items-center space-x-4">
          <Link to="/" className="flex items-center">
            <AnimatedLogo />
          </Link>
          <div className="hidden md:flex space-x-4">
            <Link to="/price" className="hover:text-orange-500">
              <FaThLarge className="inline mr-1" /> Каталог
            </Link>
            <Link to="/about" className="hover:text-orange-500">
              <FaStore className="inline mr-1" /> Магазины
            </Link>
          </div>
        </div>
        <form
          onSubmit={handleSearch}
          className="flex mx-auto w-full max-w-lg md:flex"
        >
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Поиск товара..."
            className="w-full border rounded-l-full
          px-3 py-1.5 md:px-4 md:py-2
         text-sm md:text-base         focus:outline-none focus:border-orange-400"
          />
          <button
            type="submit"
            className="bg-orange-500 text-white px-4 rounded-r-full hover:bg-orange-600"
          >
            <FaSearch />
          </button>
        </form>
        <div className="flex space-x-3 items-center">
          <Link to="/cart">
            <FaShoppingBag
              size={20}
              className="hidden md:block cursor-pointer justify-center"
            />
          </Link>
          <div className="relative justify-center">
            <FaUserCircle
              size={24}
              className="cursor-pointer"
              onClick={() =>
                user ? setDropdownOpen(!dropdownOpen) : setShowAuth(true)
              }
            />
            {user && dropdownOpen && (
              <div
                ref={dropdownRef}
                className="absolute right-0 mt-2 bg-white border rounded-lg shadow-lg w-60 z-50 p-4 space-y-3"
              >
                <p className="text-sm text-gray-700 mb-2">
                  Привет, <b>{user.login}</b>
                </p>
                <Link
                  to="/Profile"
                  className="block w-full text-center py-2 px-4 bg-gray-100 rounded-lg text-sm hover:bg-gray-200 transition"
                >
                  Личный кабинет
                </Link>
                {user && ADMIN_ROLES.includes(Number(user.role_id)) && (
                  <Link
                    to="/AdminPanel"
                    className="block w-full text-center py-2 px-4 bg-gray-100 rounded-lg text-sm hover:bg-gray-200 transition"
                  >
                    Админ панель
                  </Link>
                )}
                <button
                  onClick={handleLogout}
                  className="w-full py-2 px-4 bg-orange-500 hover:bg-orange-600 text-white text-sm rounded-lg transition"
                >
                  Выйти
                </button>
              </div>
            )}
          </div>
          <FaBars
            className="md:hidden cursor-pointer"
            onClick={() => setMenuOpen(true)}
          />
        </div>
      </div>

      {showAuth && (
        <div className="fixed inset-0 bg-black/40 flex justify-center items-center z-50">
          <div
            className="bg-white p-6 rounded shadow-lg w-96 relative"
            ref={authRef}
          >
            <button
              className="absolute top-2 right-2 text-2xl text-gray-400"
              onClick={() => setShowAuth(false)}
            >
              &times;
            </button>

            <div className="flex justify-center mb-4">
              <button
                className={`px-4 py-1 ${
                  isLogin ? "bg-orange-500 text-white" : "bg-gray-200"
                }`}
                onClick={() => setIsLogin(true)}
              >
                Вход
              </button>
              <button
                className={`px-4 py-1 ${
                  !isLogin ? "bg-orange-500 text-white" : "bg-gray-200"
                }`}
                onClick={() => setIsLogin(false)}
              >
                Регистрация
              </button>
            </div>
            <form onSubmit={handleSubmit} className="flex flex-col gap-3">
              <input
                name="login"
                type="text"
                placeholder="Логин (латиница и цифры)"
                className="border px-3 py-2 rounded"
                inputMode="latin"
                pattern="[!-~]+"
                title="Только латинские буквы, цифры и спецсимволы. Без пробелов и кириллицы."
                onInput={(e) => {
                  // удаляем любые не-ASCII символы (включая кириллицу)
                  e.currentTarget.value = e.currentTarget.value.replace(
                    /[^\x21-\x7E]/g,
                    ""
                  );
                }}
                required
              />
              <input
                name="password"
                type="password"
                placeholder="Пароль"
                className="border px-3 py-2 rounded"
                pattern="[ -~]+"
                title="Только латинские буквы, цифры и ASCII-символы (без кириллицы)"
                onInput={(e) => {
                  e.currentTarget.value = e.currentTarget.value.replace(
                    /[^\x20-\x7E]/g,
                    ""
                  );
                }}
                required
              />
              {!isLogin && (
                <>
                  <input
                    name="password2"
                    type="password"
                    placeholder="Повторите пароль"
                    className="border px-3 py-2 rounded"
                    pattern="[ -~]+"
                    title="Только латинские буквы, цифры и ASCII-символы (без кириллицы)"
                    onInput={(e) => {
                      e.currentTarget.value = e.currentTarget.value.replace(
                        /[^\x20-\x7E]/g,
                        ""
                      );
                    }}
                    required
                  />
                  {/* Галочка согласия + ссылка на документ */}
                  <label className="flex items-start gap-2 text-sm text-gray-700">
                    <input
                      name="agree"
                      type="checkbox"
                      className="mt-1 accent-orange-500"
                      required
                    />
                    <span>
                      Я соглашаюсь с{" "}
                      <Link
                        to={PRIVACY_LINK}
                        target="_blank"
                        rel="noreferrer"
                        className="text-orange-600 underline hover:no-underline"
                      >
                        политикой обработки персональных данных
                      </Link>
                      .
                    </span>
                  </label>
                </>
              )}

              {error && <p className="text-red-500 text-sm">{error}</p>}
              {success && <p className="text-green-600 text-sm">{success}</p>}
              <button
                type="submit"
                disabled={authSubmitting}
                aria-busy={authSubmitting}
                className={`relative w-full py-2 rounded font-semibold transition
    ${
      authSubmitting
        ? "bg-orange-400 cursor-wait"
        : "bg-orange-500 hover:bg-orange-600"
    }
    text-white`}
              >
                <span className="flex items-center justify-center gap-2">
                  {authSubmitting && (
                    <span
                      className="inline-block h-4 w-4 border-2 border-white/60 border-t-white rounded-full animate-spin"
                      aria-hidden="true"
                    />
                  )}
                  {isLogin
                    ? authSubmitting
                      ? "Входим…"
                      : "Войти"
                    : authSubmitting
                    ? "Регистрируем…"
                    : "Зарегистрироваться"}
                </span>
              </button>
            </form>
          </div>
        </div>
      )}
      {menuOpen && (
        <div
          ref={menuRef}
          className="fixed top-0 right-0 w-3/4 max-w-xs h-full bg-white shadow-lg z-50 transform transition-transform duration-300 translate-x-0"
        >
          <div className="flex justify-end p-4">
            <FaTimes
              className="cursor-pointer"
              onClick={() => setMenuOpen(false)}
            />
          </div>
          <nav className="px-6 space-y-4">
            <Link
              to="/price"
              className="flex items-center"
              onClick={() => setMenuOpen(false)}
            >
              <FaThLarge className="mr-2" /> Каталог
            </Link>
            <Link
              to="/about"
              className="flex items-center"
              onClick={() => setMenuOpen(false)}
            >
              <FaStore className="mr-2" /> Магазины
            </Link>
            <Link
              to="/cart"
              className="flex items-center"
              onClick={() => setMenuOpen(false)}
            >
              <FaShoppingBag className="mr-2" /> Корзина
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}
